import { ArrowRight, Info } from "lucide-react";

import { Progress } from "@/components/ui/progress";
import { ADMIN_KPI_REGISTRY } from "@/admin/config/adminKpiNaming.ts";
import { objectiveBadge } from "@/lib/businessRules";
import { formatPct0 } from "@/lib/pilotageUi";

const PIPELINE_STEPS = [
  {
    key: "entries_per_month",
    label: "Entrees / mois",
    formula: "Les entrees mensuelles alimentent le stock de ressources en fin d'annee.",
  },
  {
    key: "total_resources_end_year",
    label: "Ressources fin d'annee",
    formula: "Le total de ressources conditionne le capacitaire disponible pour facturer.",
  },
  {
    key: "total_annual_billed_etp",
    label: "ETP factures",
    formula: "Les ressources actives sont converties en ETP factures sur l'annee.",
  },
  {
    key: "annual_revenue_target",
    label: ADMIN_KPI_REGISTRY.ca_annuel_net.label,
    formula: "Le CA annuel net depend du volume d'ETP factures et du mix mission.",
  },
];

const clampProgress = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Math.max(0, Math.min(100, numericValue));
};

const statusLabel = (status) => {
  const badge = objectiveBadge(status);

  if (badge.status === "OK") {
    return "En ligne";
  }
  if (badge.status === "WARN") {
    return "A surveiller";
  }
  if (badge.status === "BAD") {
    return "Sous cible";
  }

  return "Donnees indisponibles";
};

const ObjectivesPipeline = ({ metricsByKey, loading }) => {
  return (
    <section
      className="rounded-3xl border border-white/10 glass p-6"
      data-testid="pilotage-objectives-pipeline"
      aria-labelledby="objectives-pipeline-title"
    >
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Pipeline de croissance
        </p>
        <h2 id="objectives-pipeline-title" className="mt-2 text-xl font-heading font-semibold text-white">
          Mecanique business
        </h2>
        <p className="mt-2 text-sm text-slate-400">
          De l'entree mensuelle au chiffre d'affaires annuel, suivez la chaine de conversion.
        </p>
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-slate-400">Chargement du pipeline...</p>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-3 xl:grid-cols-4">
          {PIPELINE_STEPS.map((step, index) => {
            const metric = metricsByKey[step.key];
            const badge = objectiveBadge(metric?.status);
            const progressValue = clampProgress(metric?.percent);

            return (
              <div key={step.key} className="relative">
                <article className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-white">{step.label}</p>
                    <button
                      type="button"
                      className="rounded-full border border-white/10 p-1 text-slate-400 hover:text-white"
                      title={step.formula}
                      aria-label={`Formule ${step.label}: ${step.formula}`}
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-slate-400">
                    Cible: <span className="text-slate-200">{metric?.formatter(metric?.target)}</span>
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    Actuel: <span className="text-slate-200">{metric?.formatter(metric?.realized)}</span>
                  </p>

                  <div className="mt-4 flex items-center gap-3">
                    <Progress
                      value={progressValue ?? 0}
                      className="h-2.5 flex-1 bg-white/10"
                      aria-label={`Progression pipeline ${step.label}`}
                    />
                    <span className="text-xs font-semibold text-white">
                      {metric?.percent === null || metric?.percent === undefined
                        ? "— non disponible"
                        : formatPct0(metric.percent)}
                    </span>
                  </div>

                  <p className={`mt-3 inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${badge.toneClass}`}>
                    {statusLabel(metric?.status)}
                  </p>
                </article>

                {index < PIPELINE_STEPS.length - 1 ? (
                  <ArrowRight className="pointer-events-none absolute -right-2 top-1/2 hidden h-4 w-4 -translate-y-1/2 text-slate-500 xl:block" />
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ObjectivesPipeline;
